import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import {
  Box,
  Button,
  Divider,
  ImageList,
  ImageListItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  assertNotNull,
  Languages,
  validLanguages,
} from '@remote-voice/utilities';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { AdminContent } from '@/components/atoms/AdminPageParts';
import EditButton from '@/components/atoms/EditButton';
import LoadingBackdrop from '@/components/atoms/LoadingBackdrop';
import { useCheckPermission } from '@/components/hooks/useCheckPermission';
import { useSetHeaderTitle } from '@/components/hooks/useHeaderTitle';
import BreadcrumbBar from '@/components/organisms/BreadcrumbBar';
import usePreviewImageBackdrop from '@/components/organisms/previewImageBackdrop/usePreviewImageBackdrop';
import { getAudioData } from '@/pages/admin/template/TemplateAdd';
import {
  useAdminSettingsQuery,
  useEditTemplateMessageMutation,
  useFilesQuery,
  useTemplateMessagesLazyQuery,
  useTemplateMessagesQuery,
} from '@/types/graphql';

const TemplateEdit = () => {
  useCheckPermission({ tenantCompanyAdmin: true });

  const { t, i18n } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  useSetHeaderTitle(tCommon('breadcrumb.templateAdmin'));

  const userLang = i18n.language;
  const theme = useTheme();
  const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));

  const navigate = useNavigate();
  const params = useParams();
  const templateId = params.templateId;
  assertNotNull(templateId);

  const templateMessages = useTemplateMessagesQuery({
    variables: { input: { id: templateId } },
  });
  const currentItem = templateMessages.data?.templateMessages[0];

  const adminSettings = useAdminSettingsQuery({
    variables: { input: { key: 'tts' } },
  });
  const ttsEngine = (adminSettings.data?.adminSettings?.[0]?.value ??
    'Feat') as 'Azure' | 'Feat' | 'Mimi';
  const isAzure = ttsEngine === 'Azure';

  const [rateEditingIndex, setRateEditingIndex] = useState(-1);
  const [rateEditingValue, setRateEditingValue] = useState(0);
  const [loading, setLoading] = useState(false);

  const currentItemLanguages = currentItem?.languages.sort((a, b) => {
    return (
      validLanguages.indexOf(a.language as Languages) -
      validLanguages.indexOf(b.language as Languages)
    );
  });
  const targetLanguage = currentItemLanguages?.[rateEditingIndex]?.language;

  const [getTemplateMessages] = useTemplateMessagesLazyQuery();
  const [editTemplateMessage] = useEditTemplateMessageMutation();

  // 一定時間値が変更されなければ保存処理に移る
  useEffect(() => {
    if (rateEditingIndex === -1) {
      return;
    }
    const handler = setTimeout(async () => {
      setLoading(true);
      try {
        // 保存
        {
          const result = await getTemplateMessages({
            variables: { input: { id: templateId } },
          });
          const templateMessage = result.data?.templateMessages[0];
          assertNotNull(templateMessage);
          assertNotNull(targetLanguage);
          const value = Math.max(Math.min(rateEditingValue, 2), 0.5);

          await editTemplateMessage({
            variables: {
              input: {
                id: templateId,
                templateCategoryId: templateMessage.category.id,
                languages: templateMessage.languages.map((x) => ({
                  language: x.language,
                  message: x.message,
                  readonly: x.readonly,
                  voiceRate:
                    x.language === targetLanguage ? value : x.voiceRate,
                })),
                files: templateMessage.files.map((f) => ({
                  fileName: f.fileName,
                  fileType: f.fileType,
                  id: f.id,
                })),
              },
            },
          });
        }
        //リロード
        await templateMessages.refetch();
        setRateEditingIndex(-1);
      } finally {
        setLoading(false);
      }
    }, 2000);
    return () => {
      clearTimeout(handler);
    };
  }, [
    templateId,
    rateEditingValue,
    rateEditingIndex,
    templateMessages,
    targetLanguage,
    getTemplateMessages,
    editTemplateMessage,
  ]);

  const getFiles = useFilesQuery({
    variables: {
      input: {
        ids:
          (currentItem?.files
            .map((x) => x.id)
            .filter((x) => x != null) as string[]) ?? [],
      },
    },
  });
  const previewImageBackdrop = usePreviewImageBackdrop();

  return (
    <Box>
      <LoadingBackdrop open={templateMessages.loading} />
      <BreadcrumbBar keyFrom="/admin/template" />
      <AdminContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h4">{t('template.detail')}</Typography>
          <EditButton onClick={() => navigate('edit')} />
        </Stack>

        <Stack mt={3} spacing={3}>
          <Stack spacing={1}>
            <Typography variant="h6">{t('template.category')}</Typography>
            <Typography>{currentItem?.category.name ?? ' '}</Typography>
          </Stack>
          <Divider />
          <Stack spacing={1}>
            <Typography variant="h6">{t('template.text')}</Typography>
            <Typography>
              {currentItemLanguages?.find((x) => x.language === userLang)
                ?.message ?? ' '}
            </Typography>
            <ImageList
              cols={matchDownSm ? 3 : 6}
              rowHeight={matchDownSm ? 100 : 164}
            >
              {currentItem?.files?.map((file, i) => {
                const src =
                  getFiles.data?.files.find((x) => x.id === file.id)
                    ?.signedURL ?? '';
                return (
                  <ImageListItem
                    key={i}
                    sx={{
                      border: (t) => `1px solid ${t.palette.secondary.main}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={src}
                      alt={file.fileName}
                      loading="lazy"
                      onClick={() => {
                        previewImageBackdrop.open({
                          imgProps: {
                            src: src,
                            alt: file.fileName,
                            style: {
                              maxWidth: '100%',
                              maxHeight: '100%',
                            },
                          },
                        });
                      }}
                    />
                  </ImageListItem>
                );
              }) ?? false}
            </ImageList>
          </Stack>
          <Divider />
          <Stack spacing={2}>
            <Typography variant="h6">{t('template.translation')}</Typography>
            {currentItemLanguages?.map((x, i) => (
              <Box
                key={x.language}
                display="grid"
                gridTemplateColumns="1fr auto"
              >
                <Stack spacing={1}>
                  <Typography variant="h6">
                    {tCommon(`languages.${x.language}` as any)}
                  </Typography>
                  <Box
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      px: 1,
                      backgroundColor: (t) => t.palette.common.white,
                    }}
                  >
                    <Typography>{x.message}</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Stack spacing={1}>
                    <Button
                      startIcon={<VolumeUpIcon />}
                      disabled={loading || rateEditingIndex >= 0}
                      onClick={async () => {
                        setLoading(true);
                        try {
                          // オーディオデータのダウンロード
                          const audioData = await getAudioData(
                            [{ language: x.language, message: x.message }],
                            ttsEngine,
                            x.voiceRate ?? undefined
                          );

                          // 再生
                          await new Promise<void>((resolve, reject) => {
                            Howler.stop();
                            const soundBlobUrl = URL.createObjectURL(
                              audioData[0].fileData
                            );

                            // 自動再生する設定でリロードした場合など、セキュリティー上の都合でインスタンス生成時にエラーが出る。
                            // エラーは外部でキャッチして、画面操作が必要なダイアログを表示して対応する。
                            const ttsSound = new Howl({
                              html5: true, // これを入れるとバックグラウンド再生エラーは出ない様子
                              src: [soundBlobUrl],
                              format: ['mp3'],
                              onstop: () => {
                                resolve();
                                URL.revokeObjectURL(soundBlobUrl);
                                ttsSound.unload();
                              },
                              onend: () => {
                                resolve();
                                URL.revokeObjectURL(soundBlobUrl);
                                ttsSound.unload();
                              },
                              onplayerror: () => {
                                reject();
                                URL.revokeObjectURL(soundBlobUrl);
                                ttsSound.unload();
                              },
                            });
                            ttsSound.play();
                          });
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      Play
                    </Button>
                    <Button
                      startIcon={<FileDownloadIcon />}
                      disabled={loading || rateEditingIndex >= 0}
                      onClick={async () => {
                        setLoading(true);
                        try {
                          // オーディオデータのダウンロード
                          const audioData = await getAudioData(
                            [{ language: x.language, message: x.message }],
                            ttsEngine,
                            x.voiceRate ?? undefined
                          );
                          // ローカルにダウンロード
                          const url = URL.createObjectURL(
                            audioData[0].fileData
                          );
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = audioData[0].fileName;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      DL
                    </Button>
                  </Stack>
                  {isAzure && (
                    <Stack spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        value={
                          i === rateEditingIndex
                            ? rateEditingValue
                            : x.voiceRate ?? '1.0'
                        }
                        type="number"
                        disabled={
                          loading ||
                          (rateEditingIndex >= 0 && rateEditingIndex !== i)
                        }
                        inputProps={{
                          min: 0.5,
                          max: 2.0,
                          step: '0.1',
                        }}
                        sx={{ width: 1, maxWidth: 100 }}
                        onChange={(ev) => {
                          const value = Number(ev.target.value);
                          if (isNaN(value) || value === 0) {
                            return;
                          }
                          setRateEditingIndex(i);
                          setRateEditingValue(value);
                        }}
                      />

                      <Box>再生速度</Box>
                    </Stack>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Stack>
      </AdminContent>
    </Box>
  );
};
export default TemplateEdit;
